// Rehype plugin that syntax-highlights fenced code blocks in docs markdown.
// It calls lowlight (highlight.js) directly with only the languages the docs
// use, rather than going through rehype-highlight, which statically imports
// lowlight's full `common` grammar set and would ship ~40 unused languages.
//
// A block is highlighted when its language is registered (including aliases
// such as `ts` and `html`). `text` blocks, unregistered languages, and blocks
// without a language are left as plain code. Highlighted blocks gain an `hljs`
// class and highlight.js token spans (`hljs-string`, `hljs-attr`, ...), which
// docs.css colors per reader theme.

import type { Element, ElementContent, Root, RootContent } from 'hast';
import { createLowlight } from 'lowlight';
import bash from 'highlight.js/lib/languages/bash';
import javascript from 'highlight.js/lib/languages/javascript';
import json from 'highlight.js/lib/languages/json';
import python from 'highlight.js/lib/languages/python';
import typescript from 'highlight.js/lib/languages/typescript';
import xml from 'highlight.js/lib/languages/xml';

const lowlight = createLowlight({ bash, javascript, json, python, typescript, xml });

type Node = Root | RootContent;

function languageOf(code: Element): string | undefined {
  const classes = code.properties?.className;
  if (!Array.isArray(classes)) return undefined;
  for (const name of classes) {
    const value = String(name);
    if (value.startsWith('language-')) return value.slice('language-'.length);
  }
  return undefined;
}

function textOf(node: Node): string {
  if (node.type === 'text') return node.value;
  if ('children' in node) return node.children.map(textOf).join('');
  return '';
}

function highlightCode(code: Element): void {
  const language = languageOf(code);
  if (!language || !lowlight.registered(language)) return;

  const result = lowlight.highlight(language, textOf(code));
  const classes = Array.isArray(code.properties.className)
    ? code.properties.className
    : [];
  code.properties.className = ['hljs', ...classes];
  code.children = result.children as ElementContent[];
}

function walk(node: Node, parent: Node | null): void {
  if (
    node.type === 'element' &&
    node.tagName === 'code' &&
    parent?.type === 'element' &&
    parent.tagName === 'pre'
  ) {
    highlightCode(node);
    return;
  }
  if ('children' in node) {
    for (const child of node.children) walk(child, node);
  }
}

export default function rehypeDocsHighlight() {
  return (tree: Root): void => {
    walk(tree, null);
  };
}
